import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import CompanyDocuments from "../components/CompanyDocuments";
import {
  Clock, CheckCircle2, XCircle, Loader2, ArrowRight, RefreshCw
} from "lucide-react";

// Copy aprovada pelo owner (C1009755: COPY-F4-PORTAL-PROPOSTA.md, 1.ª opção, #31–#32).
export const COPY_STATUS_SEM_SESSAO = "Para enviar os documentos do cadastro, entre no Portal com seu e-mail e senha.";
export const COPY_LINK_ENTRAR_PARA_ENVIAR = "Entrar para enviar documentos";

// ---------------------------------------------------------------------------
// Passagem do token de escopo do register para esta tela SÓ em memória do
// módulo (revisão Astra L1): nunca `navigate(…, { state })`, que grava em
// `history.pushState`. Nem o CPF/CNPJ passa (o GET autenticado devolve
// `person_type`). Vale só para o mesmo `userId`; consumida uma vez — lida no
// 1.º render e apagada depois de montar (reload ⇒ memória vazia ⇒ retomada
// pelo /login com a senha).
// ---------------------------------------------------------------------------
let pendingHandoff = null;

/** Register ⇒ status: entrega o token de escopo (ou limpa, sem token). */
export function handOffPendingDocs(userId, scopeToken) {
  pendingHandoff = userId && scopeToken ? { userId: String(userId), scopeToken } : null;
}

function peekPendingDocs(userId) {
  const h = pendingHandoff;
  return h && h.userId === String(userId) ? { scopeToken: h.scopeToken } : null;
}

function clearPendingDocs() {
  pendingHandoff = null;
}

export default function RegisterStatus() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // docs-cadastro (Escopo 2): token de escopo do register só em memória
  // (módulo, ver `handOffPendingDocs`). Nunca pelo `userId` público.
  const [pendingDocs] = useState(() => peekPendingDocs(userId));
  useEffect(() => { clearPendingDocs(); }, []);
  // Revisão Astra M3: cada leitura do cadastro (1.ª, polling e «atualizar»)
  // recarrega também a lista dos documentos (`refreshKey` do CompanyDocuments).
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/auth/register/status/${userId}`);
      setData(d);
      setDocsRefreshKey((k) => k + 1);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Registration status error:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Poll while pending
  useEffect(() => {
    if (!data || data.status !== "pending_approval") return;
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [data, load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-900">Cadastro não encontrado</h1>
          <Link to="/cadastro" className="mt-4 inline-block text-sm text-[#0055FF] hover:underline">
            Fazer novo cadastro
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = {
    pending_approval: {
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-50 border-amber-200",
      title: "Aguardando aprovação",
      message: "Seu cadastro foi enviado para análise pelo Backoffice da Zemo. Você será notificado quando aprovado.",
    },
    active: {
      icon: CheckCircle2,
      color: "text-emerald-600",
      bg: "bg-emerald-50 border-emerald-200",
      title: "Cadastro aprovado!",
      message: "Sua conta foi aprovada. Você já pode fazer login no Portal Zemo.",
    },
    rejected: {
      icon: XCircle,
      color: "text-red-600",
      bg: "bg-red-50 border-red-200",
      title: "Cadastro rejeitado",
      message: "Infelizmente seu cadastro não foi aprovado. Entre em contato com o suporte para mais informações.",
    },
  };

  const config = statusConfig[data.status] || statusConfig.pending_approval;
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="register-status-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center font-bold">Z</div>
          <div className="text-[15px] font-semibold tracking-tight">Portal Zemo</div>
        </div>

        <div className={`rounded-lg border-2 p-8 text-center ${config.bg}`} data-testid="register-status-card">
          <Icon className={`h-12 w-12 mx-auto mb-4 ${config.color}`} />
          <h1 className="text-xl font-semibold text-zinc-900" data-testid="register-status-title">
            {config.title}
          </h1>
          <p className="mt-2 text-sm text-zinc-600">{config.message}</p>

          <div className="mt-6 p-3 bg-white/60 rounded-md">
            <div className="text-xs text-zinc-500">Conta</div>
            <div className="font-semibold text-zinc-800">{data.name}</div>
            <div className="text-sm text-zinc-500">{data.email}</div>
          </div>
        </div>

        {data.status === "pending_approval" && (
          <div className="mt-6" data-testid="register-status-docs">
            {pendingDocs?.scopeToken ? (
              <CompanyDocuments mode="pendente" scopeToken={pendingDocs.scopeToken} canUpload
                                refreshKey={docsRefreshKey} />
            ) : (
              <div className="text-center text-sm text-zinc-600" data-testid="register-status-docs-sem-sessao">
                <p>{COPY_STATUS_SEM_SESSAO}</p>
                <Link to="/login" className="mt-2 inline-block text-[#0055FF] hover:underline">
                  {COPY_LINK_ENTRAR_PARA_ENVIAR}
                </Link>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col items-center gap-3">
          {data.status === "active" && (
            <Link to="/login">
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold" data-testid="register-go-login">
                Fazer login <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          )}

          {data.status === "pending_approval" && (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Verificando a cada 15 segundos...</span>
              <button onClick={load} className="text-[#0055FF] hover:underline ml-1" data-testid="register-refresh">
                <RefreshCw className="h-3 w-3" />
              </button>
            </div>
          )}

          {data.status === "rejected" && (
            <Link to="/cadastro" className="text-sm text-[#0055FF] hover:underline">
              Tentar novamente com outros dados
            </Link>
          )}

          <Link to="/login" className="text-xs text-zinc-400 hover:text-zinc-600">
            Voltar ao login
          </Link>
        </div>
      </div>
    </div>
  );
}
